import React, { useMemo } from 'react';
import { useCategoryArticles, useSubCategories } from '@retail/sanity/data-access';
import {
  CategoryListItem,
  LinkStyledListItem,
  TanstackQueryLoadingWrapper,
} from '@shared/components';
import { List, Stack, Typography } from '@mui/material';
import { useHelpCenterTFunction } from '@retail/help-center/i18n';
import { CategoryState } from './CategoryState';
import { useConsumerType, useSanityLanguageCode } from '@retail/sanity/hooks';

export interface ArticleMinimal {
  title: string;
  slug: string;
}

interface Props {
  category: CategoryState;
  onArticleClick: (article: ArticleMinimal) => void;
  onCategoryClick: (category: CategoryState) => void;
}

export const CategoryArticlesContainer: React.FC<Props> = ({
  category,
  onArticleClick,
  onCategoryClick,
}) => {
  const t = useHelpCenterTFunction();
  const language = useSanityLanguageCode();
  const consumerType = useConsumerType();

  const subCategoriesLoadingState = useSubCategories({
    language,
    categoryId: category.id,
  });

  const categoryArticlesLoadingState = useCategoryArticles({
    language,
    categoryId: category.id,
    consumer: consumerType,
  });

  const isNoContent = useMemo(() => {
    return (
      subCategoriesLoadingState.isSuccess &&
      subCategoriesLoadingState.data.length === 0 &&
      categoryArticlesLoadingState.isSuccess &&
      categoryArticlesLoadingState.data.length === 0
    );
  }, [
    categoryArticlesLoadingState?.data?.length,
    categoryArticlesLoadingState.isSuccess,
    subCategoriesLoadingState?.data?.length,
    subCategoriesLoadingState.isSuccess,
  ]);

  return (
    <Stack>
      {/* @ts-ignore */}
      <TanstackQueryLoadingWrapper loadingState={subCategoriesLoadingState}>
        {(subCategories) => {
          if (!subCategories.length) {
            return null;
          }
          return (
            <Stack>
              <Typography variant="h4" component="h2" sx={{ p: 2, pb: 0 }}>
                {t('helpCenter.subCategories')}
              </Typography>
              <List>
                {subCategories
                  .filter(({ publishTo }) => publishTo.includes(consumerType))
                  .map((category) => (
                    <CategoryListItem onClick={() => onCategoryClick(category)} key={category.id}>
                      {category.title}
                    </CategoryListItem>
                  ))}
              </List>
            </Stack>
          );
        }}
      </TanstackQueryLoadingWrapper>
      {/*@ts-ignore*/}
      <TanstackQueryLoadingWrapper loadingState={categoryArticlesLoadingState}>
        {(articles) => {
          if (!articles.length) {
            return null;
          }
          return (
            <Stack>
              <Typography variant="h4" component="h2" sx={{ p: 2, pb: 0 }}>
                {t('helpCenter.articles')}
              </Typography>
              <List>
                {articles.map((article) => (
                  <LinkStyledListItem onClick={() => onArticleClick(article)} key={article.slug}>
                    {article.title}
                  </LinkStyledListItem>
                ))}
              </List>
            </Stack>
          );
        }}
      </TanstackQueryLoadingWrapper>
      {isNoContent && (
        <Typography variant="h4" component="h2" sx={{ p: 2, pb: 0 }}>
          {t('helpCenter.noContent')}
        </Typography>
      )}
    </Stack>
  );
};
